/**
 * OrnamentBudget (ui-ux §19.0.3, §19.0.5; art §9.2): grants the scarce ornaments at layout time — the lombard to the
 * first visible Rubricated panel, ≤ 1 corner-flourish set, alternating penwork, ≤ 2 running sheens — and audits a
 * viewport (`__marg.ui.audit().ornament`): ornament and plate area ratios, element counts and cap violations.
 * Owner: UI/Visual (F3).
 */

export interface OrnamentAudit {
  viewportArea: number;
  ornamentArea: number; ornamentRatio: number;
  plateArea: number; plateRatio: number;
  counts: Record<string, number>;
  sheens: number;
  violations: string[];
}

const CAPS: Record<string, number> = { illum: 2, plate: 4, lombard: 1, flourish: 1, dropcap: 2, penwork: 3, drollery: 3, deckle: 1, sheen: 2 };

function visibleRect(el: Element, vw: number, vh: number): DOMRect | null {
  const r = el.getBoundingClientRect();
  if (r.width <= 0 || r.height <= 0 || r.bottom <= 0 || r.right <= 0 || r.top >= vh || r.left >= vw) return null;
  const cs = getComputedStyle(el);
  if (cs.visibility === 'hidden' || cs.display === 'none' || Number(cs.opacity) === 0) return null;
  return r;
}

const clipArea = (r: DOMRect, vw: number, vh: number): number =>
  Math.max(0, Math.min(r.right, vw) - Math.max(r.left, 0)) * Math.max(0, Math.min(r.bottom, vh) - Math.max(r.top, 0));

/** Grants lombard / flourish / penwork alternation / sheen slots inside `root` (call after layout and on scroll end). */
export function grantOrnaments(root: ParentNode = document, maxSheens = 2): void {
  const vw = innerWidth, vh = innerHeight;
  const panels = [...root.querySelectorAll<HTMLElement>('.mc-tier-rubric, dialog.mc-sheet[open]')];
  let lombardGiven = false, alt = false;
  for (const p of panels) {
    if (p.matches('.mc-tier-rubric')) {
      if (p.dataset.penwork !== 'none') { if (alt) p.dataset.penwork = 'alt'; else delete p.dataset.penwork; alt = !alt; }
    }
    if (p.dataset.lombardFixed !== undefined) continue;
    const vis = visibleRect(p, vw, vh);
    if (vis && !lombardGiven && p.matches('.mc-tier-rubric')) { if (!p.dataset.lombard) p.dataset.lombard = 'red'; lombardGiven = true; }
    else if (p.matches('.mc-tier-rubric')) delete p.dataset.lombard;
  }
  const flourished = [...root.querySelectorAll<HTMLElement>('[data-flourish]')];
  for (const f of flourished.slice(1)) f.removeAttribute('data-flourish');
  const sheens = [...root.querySelectorAll<HTMLElement>('[data-sheen="on"], [data-sheen="want"]')];
  let running = 0;
  for (const s of sheens) {
    if (visibleRect(s, vw, vh) && running < maxSheens) { s.dataset.sheen = 'on'; running++; } else s.dataset.sheen = 'want';
  }
}

/** Sums ornament areas per §19.0.5 (frames = perimeter × band; penwork = length × 12; arcade 6 % of the cell …). */
export function auditOrnament(root: ParentNode = document, opts: { tabRoot?: boolean; desk?: boolean } = {}): OrnamentAudit {
  const vw = innerWidth, vh = innerHeight;
  const area = vw * vh;
  let orn = 0, plate = 0;
  const counts: Record<string, number> = {};
  const bump = (k: string): void => { counts[k] = (counts[k] ?? 0) + 1; };
  for (const el of root.querySelectorAll<HTMLElement>('[data-orn]')) {
    const r = visibleRect(el, vw, vh);
    if (!r) continue;
    const kind = el.dataset.orn!;
    const a = clipArea(r, vw, vh);
    switch (kind) {
      case 'frame': {
        const band = Number.parseFloat(getComputedStyle(el).borderTopWidth) || 16;
        orn += 2 * (r.width + r.height) * band; bump('illum'); break;
      }
      case 'penwork': orn += r.width * 12; bump('penwork'); break;
      case 'plate': plate += a; bump('plate'); break;
      case 'arcade': orn += a * 0.06; bump('arcade'); break;
      case 'deckle': orn += r.width * 6; bump('deckle'); break;
      default: orn += a; bump(kind);
    }
    if (el.dataset.lombard) { orn += 40 * 40; bump('lombard'); }
    if (el.hasAttribute('data-flourish')) { orn += 4 * 24 * 24; bump('flourish'); }
  }
  const sheens = [...root.querySelectorAll('[data-sheen="on"]')].filter((e) => visibleRect(e, vw, vh)).length;
  counts.sheen = sheens;
  const violations: string[] = [];
  for (const [k, cap] of Object.entries(CAPS)) if ((counts[k] ?? 0) > cap) violations.push(`${k}: ${counts[k]} > ${cap}`);
  const ratio = orn / area, pRatio = plate / area;
  const maxOrn = opts.desk ? 0.2 : 0.15;
  if (ratio > maxOrn) violations.push(`ornament ${(ratio * 100).toFixed(1)} % > ${maxOrn * 100} %`);
  if (opts.tabRoot && ratio < 0.05) violations.push(`ornament ${(ratio * 100).toFixed(1)} % < 5 % on a tab root`);
  if (pRatio > (opts.tabRoot || opts.desk ? 0.25 : 0.12)) violations.push(`plates ${(pRatio * 100).toFixed(1)} % over budget`);
  return { viewportArea: area, ornamentArea: Math.round(orn), ornamentRatio: ratio, plateArea: Math.round(plate), plateRatio: pRatio, counts, sheens, violations };
}
